// SurfaceComments — a low-friction "leave a note for the coach" affordance on a
// Day surface (activity / macros / weight). Shows prior comments for the date +
// surface and a small input to add one. Adding a comment does NOT trigger a
// coach call — comments accumulate and feed the next coach call as context.
//
// Mounted on MorningWeight, MacroTotals, and WorkoutAnchor, scoped by
// surface_type + the active date.

import { useState } from 'react';

import { C, FONT_MONO, LABEL_TRACKING } from '../../lib/design';
import { useSurfaceComments } from '../../lib/day-data';
import type { SurfaceType } from '../../lib/types';

// The surface name shown in the collapsed affordance so the operator can tell
// which datum a note attaches to (e.g. "+ NOTE ON WEIGHT FOR COACH").
const SURFACE_LABEL: Record<SurfaceType, string> = {
  weight: 'WEIGHT',
  macros: 'MACROS',
  activity: 'ACTIVITY',
};

export function SurfaceComments({
  date,
  surface,
  isToday = true,
}: {
  date: string;
  surface: SurfaceType;
  isToday?: boolean;
}) {
  const { comments, add, isAdding } = useSurfaceComments(date);
  const mine = comments.filter((c) => c.surface_type === surface);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const accent = isToday ? C.signal : C.warn;

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    // Clear the draft only after the POST resolves — a failed write keeps the
    // operator's text so they can retry instead of losing it.
    try {
      await add(surface, t.slice(0, 2000));
      setText('');
    } catch {
      // Leave `text` intact; the failed-write retry queue in apiClient also
      // captures the POST, and isAdding has reset so the operator can resubmit.
    }
  };

  return (
    // A left rule binds the affordance to the panel it sits beneath, so the
    // note clearly belongs to the datum above it rather than floating midway to
    // the next panel. A small top inset reads as "tucked just under" the panel
    // without crowding its border.
    <div
      style={{
        marginTop: 6,
        paddingLeft: 10,
        borderLeft: `1px solid ${C.line}`,
      }}
    >
      {/* Prior comments for this surface/date. */}
      {mine.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          {mine.map((c) => (
            <div
              key={c.id}
              style={{
                fontSize: 11,
                color: C.dim,
                fontStyle: 'italic',
                lineHeight: 1.5,
                marginBottom: 4,
              }}
            >
              “{c.text}”
            </div>
          ))}
        </div>
      )}

      {open ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
            }}
            placeholder="Note for the coach…"
            maxLength={2000}
            rows={2}
            autoFocus
            style={{
              flex: 1,
              background: C.panel2,
              border: `1px solid ${C.line}`,
              color: C.text,
              fontFamily: FONT_MONO,
              fontSize: 11,
              padding: '6px 8px',
              resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button
              type="button"
              onClick={submit}
              disabled={isAdding || text.trim().length === 0}
              style={{
                background: accent,
                color: C.bg,
                border: 'none',
                fontFamily: FONT_MONO,
                fontSize: 10,
                letterSpacing: LABEL_TRACKING,
                padding: '4px 8px',
                cursor: 'pointer',
              }}
            >
              ADD
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setText(''); }}
              style={{
                background: 'transparent',
                color: C.faint,
                border: `1px solid ${C.line}`,
                fontFamily: FONT_MONO,
                fontSize: 10,
                padding: '4px 8px',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            background: 'transparent',
            color: C.faint,
            border: 'none',
            fontFamily: FONT_MONO,
            fontSize: 10,
            letterSpacing: LABEL_TRACKING,
            padding: 0,
            cursor: 'pointer',
          }}
        >
          + NOTE ON {SURFACE_LABEL[surface]} FOR COACH
        </button>
      )}
    </div>
  );
}
